import { filterClasses } from "@swivl-mono/helpers";
import { PolicyFieldOption, PolicyFieldType } from "@swivl-mono/types";
import { Checkbox, Popover, PopoverPosition, Switch, SwitchVariant } from "@swivl-mono/ui";
import { useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import { PolicyRadio } from "../../Properties/PolicyWorksheets/PolicyRadio";
import { displayPolicyValue } from "./PolicyUpdatesList";

export interface PolicyValueControlProps {
  fieldType?: PolicyFieldType;
  options?: PolicyFieldOption[];
  value: string;
  busy: boolean;
  onChange: (value: string) => void;
}

/**
 * The field's REAL Policy Builder control — toggle → Switch, single-select → PolicyRadio,
 * multi-select → a checkbox list, text/number/unknown → the original click-to-edit text input —
 * so reviewing a value here feels like using the Policy page itself, not a separate free-text
 * editor an operator can type the wrong answer into. Pure and decoupled from PolicyReviewGroup
 * (plain fieldType/options/value/onChange) so it's reusable both for a group-wide edit (when every
 * location currently agrees on one value) and a single location's own edit inside the per-location
 * breakdown ("After apply") — the two places PolicyWalkThrough.tsx and PolicyUpdatesList.tsx each
 * need it. Extracted to its own file (rather than living in either of those) because they already
 * import from one another one direction (PolicyWalkThrough → PolicyUpdatesList); a third shared
 * home avoids turning that into a cycle.
 */
export function PolicyValueControl({ fieldType, options, value, busy, onChange }: PolicyValueControlProps): React.ReactElement {
  if (fieldType === "toggle") {
    // stopPropagation here, not on an ancestor wrapper — a caller with a click-to-expand row (e.g.
    // PolicyUpdateRow) needs clicking the switch itself to toggle it, not also expand/collapse the row.
    return (
      <span onClick={(event) => event.stopPropagation()}>
        <Switch
          variant={SwitchVariant.TOGGLE}
          activeColor="orange"
          on={value === "1"}
          isDisabled={busy}
          onSet={(next) => onChange(next ? "1" : "0")}
        />
      </span>
    );
  }

  if (fieldType === "single-select" && options && options.length > 0) {
    return (
      <PolicyRadio
        value={value}
        disabled={busy}
        options={options.map((option) => ({ label: option.label, value: option.optionKey }))}
        onChange={onChange}
      />
    );
  }

  if (fieldType === "multi-select" && options && options.length > 0) {
    const selected = new Set(value.split(",").filter((token) => token !== ""));
    return (
      <div className="flex flex-col gap-2" role="group">
        {options.map((option) => (
          <Checkbox
            key={option.optionKey}
            label={option.label}
            disabled={busy}
            checked={selected.has(option.optionKey)}
            onCheckedChange={(checked) => {
              const next = new Set(selected);
              if (checked === true) next.add(option.optionKey);
              else next.delete(option.optionKey);
              // Preserve definition order in the comma-joined value.
              const ordered = options.map((o) => o.optionKey).filter((key) => next.has(key));
              onChange(ordered.join(","));
            }}
          />
        ))}
      </div>
    );
  }

  return <FreeValueInput fieldType={fieldType} options={options} value={value} busy={busy} onChange={onChange} />;
}

/**
 * Same field-type dispatch as `PolicyValueControl`, but a single-/multi-select never shows every
 * option up front — only the one value that currently applies, as a compact clickable chip;
 * clicking it opens the real picker (a radio list or checkboxes) in a popover. This is what the
 * per-location "After apply" breakdown uses (AffectedLocationsDetail) — showing every possible
 * option for every location at once was too much given there's one row per location already.
 * Toggle/text/number are already exactly this shape (one compact value, click to change it), so
 * they pass straight through to `PolicyValueControl` unchanged.
 */
export function CompactPolicyValueControl({ fieldType, options, value, busy, onChange }: PolicyValueControlProps): React.ReactElement {
  if ((fieldType === "single-select" || fieldType === "multi-select") && options && options.length > 0) {
    return <PickerPopover fieldType={fieldType} options={options} value={value} busy={busy} onChange={onChange} />;
  }
  return <PolicyValueControl fieldType={fieldType} options={options} value={value} busy={busy} onChange={onChange} />;
}

function PickerPopover({ fieldType, options, value, busy, onChange }: PolicyValueControlProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const display = displayPolicyValue(value, fieldType, options);

  return (
    // stopPropagation wraps trigger AND content together — a Popover's content still renders as a
    // React descendant of this span despite portalling elsewhere in the DOM, so a click anywhere in
    // the open picker (not just the trigger) needs catching here to keep a click-to-expand row
    // ancestor (PolicyUpdateRow) from also toggling on every pick.
    <span onClick={(event) => event.stopPropagation()}>
      <Popover
        isOpen={open}
        onOpenChange={setOpen}
        disabled={busy}
        side="bottom"
        position={PopoverPosition.LEFT}
        className="w-72 p-3"
        trigger={
          <button
            type="button"
            disabled={busy}
            title="Click to edit"
            className={filterClasses(
              "flex min-w-0 items-center gap-1 text-[13px] font-semibold text-grey-900",
              !busy && "cursor-pointer hover:text-orange-700"
            )}
          >
            <span className="truncate">{display}</span>
            <FiChevronDown className="size-3 shrink-0 text-grey-400" />
          </button>
        }
      >
        {fieldType === "single-select" ? (
          <PolicyRadio
            value={value}
            disabled={busy}
            options={(options ?? []).map((option) => ({ label: option.label, value: option.optionKey }))}
            onChange={(next) => {
              onChange(next);
              setOpen(false); // one choice fully answers a single-select — close right away
            }}
          />
        ) : (
          <div className="flex flex-col gap-2" role="group">
            {(options ?? []).map((option) => {
              const selected = new Set(value.split(",").filter((token) => token !== ""));
              return (
                <Checkbox
                  key={option.optionKey}
                  label={option.label}
                  disabled={busy}
                  checked={selected.has(option.optionKey)}
                  onCheckedChange={(checked) => {
                    const next = new Set(selected);
                    if (checked === true) next.add(option.optionKey);
                    else next.delete(option.optionKey);
                    // Preserve definition order in the comma-joined value.
                    const ordered = (options ?? []).map((o) => o.optionKey).filter((key) => next.has(key));
                    onChange(ordered.join(","));
                    // Left open — a multi-select is usually more than one pick; closes on click-away.
                  }}
                />
              );
            })}
          </div>
        )}
      </Popover>
    </span>
  );
}

/** text/number/unknown fieldType — the original click-to-edit text input, unchanged. */
export function FreeValueInput({ fieldType, options, value, busy, onChange }: PolicyValueControlProps): React.ReactElement {
  const display = displayPolicyValue(value, fieldType, options);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(display);

  const commit = (): void => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== display) onChange(trimmed);
    else setDraft(display);
  };

  return editing ? (
    <input
      autoFocus
      value={draft}
      disabled={busy}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        else if (event.key === "Escape") {
          setDraft(display);
          event.currentTarget.blur();
        }
      }}
      onBlur={commit}
      className="h-8 w-full rounded-[4px] border-2 border-orange-500 bg-white px-2.5 text-[14px] outline-none"
    />
  ) : (
    <span
      onClick={
        busy
          ? undefined
          : (event) => {
              event.stopPropagation();
              setEditing(true);
            }
      }
      title="Click to edit"
      className={filterClasses("text-[15px]", display ? "font-semibold text-grey-900" : "text-grey-500 italic", !busy && "cursor-pointer")}
    >
      {display || "Not set, click to add"}
    </span>
  );
}
